import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Check, Handshake, Flame } from "lucide-react";

export const PromiseTracker = ({ onTick }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [celebrate, setCelebrate] = useState(null);

  useEffect(() => {
    api.get("/promise/current").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data?.promise) return null;

  const tick = async (d) => {
    if (d.is_future || busy) return;
    setBusy(true);
    try {
      const prev = data;
      const { data: next } = await api.post("/promise/tick", { date: d.date });
      setData(next);
      if (!prev.streak_includes_this_week && next.streak_includes_this_week) {
        setCelebrate(next.weeks_streak);
        setTimeout(() => setCelebrate(null), 6000);
      }
      onTick?.();
    } finally {
      setBusy(false);
    }
  };

  const hit = data.kept_count >= 5;
  const streak = data.weeks_streak || 0;

  return (
    <section className="card mb-8 relative overflow-hidden" data-testid="promise-tracker" style={{ borderColor: hit ? "#FF0055" : undefined }}>
      {celebrate && (
        <div className="absolute inset-0 z-20 flex items-center justify-center fade-up" style={{ background: "rgba(10,10,10,0.92)" }} data-testid="promise-celebrate">
          <div className="text-center px-6">
            <Flame size={36} color="#FF0055" className="mx-auto" />
            <p className="eyebrow mt-3" style={{ color: "#FF0055" }}>Promise Keeper</p>
            <p className="font-display font-black text-2xl md:text-4xl mt-2 uppercase">
              {celebrate} {celebrate === 1 ? "week" : "weeks"} in a row
            </p>
            <p className="text-sm text-zinc-400 mt-2">You kept your word to yourself. That's the whole point.</p>
          </div>
        </div>
      )}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow flex items-center gap-1.5" style={{ color: "#FF0055" }}>
            <Handshake size={11} strokeWidth={2.5} /> This week's one promise
          </p>
          <p className="font-display font-black text-lg md:text-2xl mt-2 uppercase leading-tight" data-testid="promise-text">
            {data.promise}
          </p>
        </div>
        <div className="flex gap-6 text-right">
          <div data-testid="promise-streak">
            <p className="eyebrow flex items-center gap-1 justify-end"><Flame size={11} strokeWidth={2.5} color={streak > 0 ? "#FF0055" : undefined} /> Streak</p>
            <p className="font-display font-black text-3xl mt-1" style={{ color: streak > 0 ? "#FF0055" : "white" }}>
              {streak}<span className="text-zinc-600 text-sm ml-1">{streak === 1 ? "wk" : "wks"}</span>
            </p>
          </div>
          <div>
            <p className="eyebrow">Kept</p>
            <p className="font-display font-black text-3xl mt-1" style={{ color: data.kept_count > 0 ? "#FF0055" : "white" }} data-testid="promise-kept-count">
              {data.kept_count}<span className="text-zinc-600 text-sm">/7</span>
            </p>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-2 mt-5" data-testid="promise-days">
        {data.days.map((d) => (
          <button
            key={d.date}
            onClick={() => tick(d)}
            disabled={d.is_future || busy}
            data-testid={`promise-day-${d.label.toLowerCase()}`}
            className="flex flex-col items-center gap-2 py-3 transition-colors disabled:cursor-not-allowed"
            style={{
              background: d.kept ? "#FF0055" : "#141414",
              border: `1px solid ${d.kept ? "#FF0055" : d.is_today ? "#FFFFFF" : "#27272A"}`,
              opacity: d.is_future ? 0.35 : 1,
            }}
          >
            <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: d.kept ? "white" : "#a1a1aa" }}>{d.label}</span>
            <span className="w-5 h-5 flex items-center justify-center">
              {d.kept ? <Check size={16} strokeWidth={3} color="white" /> : <span className="w-1.5 h-1.5 rounded-full" style={{ background: d.is_today ? "white" : "#3f3f46" }} />}
            </span>
          </button>
        ))}
      </div>
      <p className="text-xs text-zinc-500 mt-4">
        {hit
          ? streak > 1 ? `Promise Keeper, ${streak} weeks running. That's a pattern, not a fluke.` : "Promise Keeper. That's proof you can trust."
          : streak > 0 ? `${streak}-week run on the line. 5 of 7 keeps it alive.` : "Tick each day you kept it. 5 of 7 earns Promise Keeper."}
        {" "}<Link to="/checkin" className="underline hover:text-white">From your check-in →</Link>
      </p>
    </section>
  );
};
